import { useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, X as XIcon, ShieldCheck, PhoneCall, Compass } from "lucide-react";
import CoachingJourney from "@/components/CoachingJourney";
import Booking from "@/components/Booking";
import Footer from "@/components/Footer";

const forYou = [
  "Controllers and Accounting Managers ready for the executive seat",
  "Senior Accountants and FP&A leads who want to move beyond reporting",
  "Immigrant and internationally trained finance professionals building a US career",
  "Finance professionals who are technically strong but want strategy, communication, and leadership",
  "Anyone preparing for a CFO interview in the next 12 months",
];

const notForYou = [
  "Those looking for basic accounting or bookkeeping training",
  "Anyone expecting a job placement service — this builds the leader, not the listing",
  "Professionals unwilling to do the between-session exercises and simulations",
];

const promises = [
  { title: "Session 1 Guarantee", text: "If after Session 1 you don't see clear value, you pay only for that single session ($200), and the remainder of the program is fully refunded." },
  { title: "Deliverables in writing", text: "Every session ends with a named toolkit — scorecards, templates, checklists, and plans you keep forever." },
  { title: "Real CFO simulations", text: "Case studies and board-room simulations drawn from real experience leading finance for organizations up to $46M." },
  { title: "Direct mentor access", text: "You work one-on-one with Zecharias — no group calls, no pre-recorded videos, no assistants." },
];

export default function CFOAccelerator() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "The CFO Accelerator™ — BookCare LLC";
    if (!sessionStorage.getItem("bc_accel_tracked")) {
      axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/track`, { page: "cfo-accelerator" }).catch(() => {});
      sessionStorage.setItem("bc_accel_tracked", "1");
    }
    return () => { document.title = "BookCare LLC — Bookkeeping, Tax Preparation & Fractional CFO Services"; };
  }, []);

  return (
    <div className="bg-bone text-pine" data-testid="accelerator-page">
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-linen bg-white/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-10">
          <Link to="/" data-testid="accelerator-back-link" className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-pine/70 transition-colors hover:text-forest">
            <ArrowLeft size={14} /> BookCare LLC
          </Link>
          <a
            href="#booking"
            data-testid="accelerator-nav-cta"
            className="border border-forest bg-forest px-6 py-2.5 font-mono text-xs uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-transparent hover:text-forest"
          >
            Book a Strategy Call
          </a>
        </div>
      </header>

      <section className="border-b border-linen bg-pine pt-32 pb-24 text-bone md:pt-40 md:pb-32">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="mb-6 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.35em] text-terra"
          >
            <Compass size={14} /> 5-Session Signature CFO Coaching Program
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
            className="mb-8 max-w-4xl font-serif text-4xl font-medium leading-tight tracking-tight sm:text-5xl lg:text-6xl"
            data-testid="accelerator-heading"
          >
            The CFO <span className="italic text-terra">Accelerator</span>&trade;
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-10 max-w-2xl text-lg leading-relaxed text-bone/75"
          >
            From Controller to Strategic CFO: learn how to think, operate, communicate, and lead
            at the CFO level — coached one-on-one by a mentor who has held the seat.
          </motion.p>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.4 }} className="flex flex-wrap items-center gap-5">
            <a
              href="#booking"
              data-testid="accelerator-hero-cta"
              className="inline-flex items-center gap-2 border border-terra bg-terra px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-transparent hover:text-terra"
            >
              Book your free strategy call <ArrowRight size={16} />
            </a>
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-bone/50">5 sessions · $4,989 · or $200 per session</p>
          </motion.div>
        </div>
      </section>

      <section className="border-b border-linen" data-testid="accelerator-who-section">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-32">
          <p className="mb-6 font-mono text-[11px] uppercase tracking-[0.35em] text-terra">Who This Is For</p>
          <h2 className="mb-16 max-w-2xl font-serif text-4xl font-medium leading-tight tracking-tight text-pine md:text-5xl">
            Built for the finance professional <span className="italic text-forest">one seat away</span>.
          </h2>
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            <div className="border border-linen bg-white p-8 md:p-10" data-testid="accelerator-for-you">
              <p className="mb-6 font-mono text-[10px] uppercase tracking-[0.25em] text-forest">This is for you if…</p>
              <ul className="flex flex-col gap-4">
                {forYou.map((t) => (
                  <li key={t} className="flex items-start gap-3 text-sm leading-relaxed text-pine/80">
                    <Check size={15} className="mt-0.5 shrink-0 text-forest" /> {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="border border-linen bg-white/60 p-8 md:p-10" data-testid="accelerator-not-for-you">
              <p className="mb-6 font-mono text-[10px] uppercase tracking-[0.25em] text-terra">This is not for you if…</p>
              <ul className="flex flex-col gap-4">
                {notForYou.map((t) => (
                  <li key={t} className="flex items-start gap-3 text-sm leading-relaxed text-pine/60">
                    <XIcon size={15} className="mt-0.5 shrink-0 text-terra" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <CoachingJourney showPageLink={false} />

      <section className="border-t border-linen bg-forest text-bone" data-testid="accelerator-guarantee-section">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-32">
          <p className="mb-6 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.35em] text-terra">
            <ShieldCheck size={14} /> Our Promise
          </p>
          <h2 className="mb-16 max-w-2xl font-serif text-4xl font-medium leading-tight tracking-tight md:text-5xl">
            What you can <span className="italic text-terra">hold us to</span>.
          </h2>
          <div className="grid grid-cols-1 gap-px border border-bone/10 bg-bone/10 sm:grid-cols-2">
            {promises.map((p, i) => (
              <div key={p.title} className="bg-forest p-8 md:p-10" data-testid={`accelerator-promise-${i}`}>
                <h3 className="mb-3 font-serif text-xl font-semibold">{p.title}</h3>
                <p className="text-sm leading-relaxed text-bone/70">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-linen bg-white/40" data-testid="accelerator-strategy-call">
        <div className="mx-auto max-w-7xl px-6 pt-24 md:px-10 md:pt-32">
          <p className="mb-6 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.35em] text-terra">
            <PhoneCall size={14} /> Free CFO Strategy Call
          </p>
          <h2 className="mb-6 max-w-2xl font-serif text-4xl font-medium leading-tight tracking-tight text-pine md:text-5xl">
            Not sure yet? <span className="italic text-forest">Talk first.</span>
          </h2>
          <p className="max-w-xl text-sm leading-relaxed text-pine/60">
            Book a free 30-minute strategy call. We&apos;ll assess where you are, whether the
            Accelerator fits, and what your fastest path to the CFO seat looks like — no
            obligation either way.
          </p>
        </div>
        <Booking />
      </section>

      <Footer />
    </div>
  );
}
